import { compose } from 'redux';
import { connect } from 'react-redux';
import Button from '@mui/material/Button';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import LinearProgress from '@mui/material/LinearProgress';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Paper from '@mui/material/Paper';
import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import { withTranslation } from 'react-i18next';
import withPermCalc, { licenseManufacturerRouteName } from '../../HOCs/withPermCalc';

import {
    fetchDeviceManufacturers,
    saveDeviceManufacturer,
    deleteDeviceManufacturer,
    updateDeviceManufacturer
} from '../../../actions/deviceManufacturer';
import EnhancedTableHead, { SmallerTableCell } from '../../Shared/EnhancedTableHead';
import { stableSort, getSorting, PersistentPaginationModel } from '../../../utils';
import AlertDialog from '../../Shared/AlertDialog';
import CommonFab from '../../Shared/CommonFab';
import ConfirmationDialog from '../../Shared/ConfirmationDialog';
import EmptyTableNotification from '../../Shared/EmptyTableNotification';
import withTableHints from '../../HOCs/withTableHints';
import withTableStyles from '../../HOCs/withTableStyles';
import DeviceManufacturerAdd from '../DeviceManufacturersAdd/DeviceManufacturerAdd';
import TablePaginationActions from '../../Shared/TablePaginationActions';

const headerRows = [
    {
        id: 'name',
        numeric: false,
        disableSorting: false,
        disablePadding: false,
        label: 'deviceManufacturer',
    },
    {
        id: 'numberOfDevices',
        numeric: false,
        disableSorting: false,
        disablePadding: false,
        label: 'numberOfDevices',
    }
];

const headerProtectedRows = [
    { id: 'actions', numeric: false, disableSorting: true, disablePadding: false, label: 'actions' }
];

const pageName = 'DEVICE_MANUFACTURERS';
const persistentPaginationModel = new PersistentPaginationModel(pageName);

class DeviceManufacturersList extends Component {
    state = {
        order: 'asc',
        orderBy: 'name',
        page: 0,
        rowsPerPage: persistentPaginationModel.getItemsPerPage(),
        open: false,

        manufacturerId: '',
        manufacturer: {},
        isCreateDialog: false,

        showAlert: false,
        alertDescription: '',
        alertVariant: 'info',

        showConfirmation: false,

        anchorEl: null
    };

    constructor(props) {
        super(props);

        const { editPerm } = this.props;

        if (editPerm) {
            this.headerRows = [...headerRows, ...headerProtectedRows];
        } else {
            this.headerRows = headerRows;
        }
    }

    componentDidMount() {
        this.props.fetchDeviceManufacturers();
    }

    handleClickAdd = () => {
        this.setState({
            manufacturerId: '',
            manufacturer: {},
            isCreateDialog: true,
            open: true
        });
    };

    handleClickDeleteConfirmation = () => {
        this.setState({ showConfirmation: true });
    };

    handleClickDelete = async () => {
        const { manufacturerId } = this.state;
        const { t } = this.props;
        const manufacturer = this.props.manufacturers.find(type => type.id === manufacturerId);

        this.setState({ showConfirmation: false });

        if (!manufacturer) {
            this.setState({
                alertDescription: t('errorCantFindDeviceManufacturer'),
                alertVariant: 'warning',
                showAlert: true
            });
        } else if (manufacturer.numberOfDevices > 0) {
            this.setState({
                alertDescription: t('errorDeviceMoreZero', { numberOfItems: manufacturer.numberOfDevices }),
                alertVariant: 'warning',
                showAlert: true
            });
        } else if (manufacturer.numberOfDevices === 0 && !manufacturer.canBeDeleted) {
            this.setState({
                alertDescription: t('errorDeviceInArchive'),
                alertVariant: 'warning',
                showAlert: true
            });
        } else {
            try {
                await this.props.deleteDeviceManufacturer(manufacturer);
                this.setState({
                    alertDescription: t('notificationDeviceManufacturerWasDeleted'),
                    alertVariant: 'success',
                    showAlert: true
                });
            } catch (e) {
                if (this.props.error) {
                    this.setState({
                        alertDescription: this.props.error,
                        alertVariant: 'warning',
                        showAlert: true
                    });
                }
            }
        }
    };

    handleClickEdit = () => {
        const { manufacturerId } = this.state;
        const manufacturer = this.props.manufacturers.find(type => type.id === manufacturerId);

        if (!manufacturer) {
            this.setState({
                alertDescription: this.props.t('errorCantFindDeviceManufacturer'),
                alertVariant: 'warning',
                showAlert: true
            });
        } else {
            this.setState({
                manufacturer,
                isCreateDialog: false,
                open: true
            });
        }
    };

    handleClose = () => {
        this.setState({ open: false });
    };

    handleAlertClose = (e, reason) => {
        if (reason === 'clickaway') return;

        this.setState({ showAlert: false });
    };

    handleConfirmationClose = () => {
        this.setState({ showConfirmation: false });
    };

    handleCreateManufacturer = async data => {
        try {
            await this.props.saveDeviceManufacturer(data);
            this.setState({
                open: false,
                alertDescription: this.props.t('notificationDeviceManufacturerAdded'),
                alertVariant: 'success',
                showAlert: true
            });

            await this.props.fetchDeviceManufacturers();
        } catch (e) {
            if (this.props.error) {
                this.setState({
                    alertDescription: this.props.error,
                    alertVariant: 'warning',
                    showAlert: true
                });
            }
        }
    };

    handleEditManufacturer = async data => {
        const { manufacturerId } = this.state;
        const { t } = this.props;
        const manufacturer = this.props.manufacturers.find(type => type.id === manufacturerId);

        const params = {
            ...data,
            id: manufacturerId
        };

        if (!manufacturer) {
            this.setState({
                alertDescription: t('errorCantFindDeviceManufacturer'),
                alertVariant: 'warning',
                showAlert: true
            });
        } else {
            try {
                await this.props.updateDeviceManufacturer(manufacturerId, params);
                this.setState({
                    open: false,
                    manufacturer: {},
                    alertDescription: t('notificationDeviceManufacturerUpdated'),
                    alertVariant: 'success',
                    showAlert: true
                });
            } catch (e) {
                if (this.props.error) {
                    this.setState({
                        alertDescription: this.props.error,
                        alertVariant: 'warning',
                        showAlert: true
                    });
                }
            }
        }
    };

    handleRequestSort = (event, property) => {
        const orderBy = property;
        let order = 'desc';

        if (this.state.orderBy === property && this.state.order === 'desc') {
            order = 'asc';
        }

        this.setState({ order, orderBy });
    };

    handleChangePage = (event, page) => {
        this.setState({ page });
    };

    handleChangeRowsPerPage = event => {
        const rowsPerPage = event.target.value;

        this.setState({
            rowsPerPage,
            page: 0
        });

        persistentPaginationModel.setItemsPerPage(rowsPerPage);
    };

    handleMenuClick = id => event => {
        this.setState({
            manufacturerId: id,
            anchorEl: event.currentTarget
        });
    };

    handleMenuClose = () => {
        this.setState({ anchorEl: null });
    };

    _renderTable = () => {
        const { order, orderBy, rowsPerPage, page, anchorEl } = this.state;
        const { manufacturers, totalCount, classes, editPerm, t } = this.props;
        const configuredStableSort = compose(stableSort, getSorting)(order, orderBy);
        if (!totalCount) {
            return <EmptyTableNotification t={t} />;
        }

        return (
            <Fragment>
                <Paper>
                    <div className={classes.tableWrapper}>
                        <Table
                            className={classes.table}
                            stickyHeader
                        >
                            <EnhancedTableHead
                                headerRows={this.headerRows}
                                order={order}
                                orderBy={orderBy}
                                onRequestSort={this.handleRequestSort}
                                rowCount={manufacturers.length}
                                t={t}
                            />
                            <TableBody>
                                {configuredStableSort(manufacturers)
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map(manufacturer => (
                                        <TableRow
                                            className={classes.row}
                                            key={manufacturer.id}
                                        >
                                            <SmallerTableCell>{manufacturer.name}</SmallerTableCell>
                                            <SmallerTableCell>{manufacturer.numberOfDevices}</SmallerTableCell>
                                            {editPerm && (
                                                <SmallerTableCell>
                                                    <Button
                                                        color="inherit"
                                                        aria-label="Menu"
                                                        aria-owns={anchorEl ? 'simple-menu' + manufacturer.id : null}
                                                        aria-haspopup="true"
                                                        onClick={this.handleMenuClick(manufacturer.id)}
                                                    >
                                                        <MoreVertIcon />
                                                    </Button>
                                                    <Menu
                                                        disableAutoFocusItem
                                                        id={'simple-menu' + manufacturer.id}
                                                        anchorEl={anchorEl}
                                                        open={Boolean(
                                                            anchorEl && this.state.manufacturerId === manufacturer.id
                                                        )}
                                                        onClose={this.handleMenuClose}
                                                    >
                                                        <MenuItem
                                                            key={'edit' + manufacturer.id}
                                                            onClick={() => {
                                                                this.setState({ anchorEl: null }, this.handleClickEdit);
                                                            }}
                                                        >
                                                            <ListItemIcon>
                                                                <EditIcon />
                                                            </ListItemIcon>
                                                            {t('edit')}
                                                        </MenuItem>
                                                        <MenuItem
                                                            key={'delete' + manufacturer.id}
                                                            onClick={() => {
                                                                this.setState(
                                                                    { anchorEl: null },
                                                                    this.handleClickDeleteConfirmation
                                                                );
                                                            }}
                                                        >
                                                            <ListItemIcon>
                                                                <DeleteIcon />
                                                            </ListItemIcon>
                                                            {t('delete')}
                                                        </MenuItem>
                                                    </Menu>
                                                </SmallerTableCell>
                                            )}
                                        </TableRow>
                                    ))}
                            </TableBody>
                        </Table>
                    </div>
                </Paper>

                <TablePagination
                    component="div"
                    count={manufacturers.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    labelRowsPerPage={t('rowsPerPage')}
                    onPageChange={this.handleChangePage}
                    onRowsPerPageChange={this.handleChangeRowsPerPage}
                    ActionsComponent={TablePaginationActions}
                />
            </Fragment>
        );
    };

    render() {
        const { loading, addNewPerm, t } = this.props;

        return (
            <React.Fragment>
                <h2>
                    {t('deviceManufacturer')}
                </h2>

                {this.state.open && (
                    <DeviceManufacturerAdd
                        open={this.state.open}
                        clickClose={this.handleClose}
                        clickSave={
                            this.state.isCreateDialog ? this.handleCreateManufacturer : this.handleEditManufacturer
                        }
                        isLoading={loading}
                        title={
                            this.state.isCreateDialog ? t('addDeviceManufacturerTitle') : t('editDeviceManufacturerTitle')
                        }
                        data={this.state.manufacturer}
                        t={t}
                    />
                )}

                <AlertDialog
                    open={this.state.showAlert}
                    variant={this.state.alertVariant}
                    onClose={this.handleAlertClose}
                    message={this.state.alertDescription}
                />

                <ConfirmationDialog
                    open={this.state.showConfirmation}
                    TitleButtomOk="yes"
                    TitleButtomCancel="no"
                    title={t('confirmation')}
                    description={t('deleteConfirmationMessage')}
                    handleOk={this.handleClickDelete}
                    handleClose={this.handleConfirmationClose}
                    t={t}
                />

                {loading ? <LinearProgress /> : this._renderTable()}

                {addNewPerm && (
                    <CommonFab
                        tooltipTranslateId="addDeviceManufacturerTitle"
                        onClick={this.handleClickAdd}
                        t={t}
                    />
                )}
            </React.Fragment>
        );
    }
}

DeviceManufacturersList.propTypes = {
    classes: PropTypes.object.isRequired,
    fetchDeviceManufacturers: PropTypes.func.isRequired,
    deleteDeviceManufacturer: PropTypes.func.isRequired,
    updateDeviceManufacturer: PropTypes.func.isRequired,
    saveDeviceManufacturer: PropTypes.func.isRequired,
    manufacturers: PropTypes.array.isRequired,
    totalCount: PropTypes.number.isRequired,
    error: PropTypes.string.isRequired,
    loading: PropTypes.bool,
    editPerm: PropTypes.bool,
    addNewPerm: PropTypes.bool,
};

const mapStateToProps = ({ deviceManufacturers, user }) => ({
    manufacturers: deviceManufacturers.data,
    totalCount: deviceManufacturers.data ? deviceManufacturers.data.length : 0,
    error: deviceManufacturers.error,
    loading: deviceManufacturers.loading,
    perms: user.perms
});

const mapDispatchToProps = {
    fetchDeviceManufacturers,
    saveDeviceManufacturer,
    deleteDeviceManufacturer,
    updateDeviceManufacturer
};

export default compose(
    withTranslation('deviceManufacturers'),
    connect(mapStateToProps, mapDispatchToProps),
    withTableStyles(),
    withPermCalc(licenseManufacturerRouteName),
    withTableHints
)(DeviceManufacturersList);
